/**
 * Hook d'auto-save debounced — RG-SAIS-01.
 *
 * Surveille `payload` et, après `delayMs` d'inactivité, déclenche `saveFn`.
 * Renvoie un statut lisible pour afficher "Sauvegarde…" / "Sauvegardé" /
 * "Erreur de sauvegarde".
 *
 * Robustesse :
 * - Retry automatique avec back-off exponentiel sur erreur réseau / serveur
 *   (5xx). Les erreurs 4xx (validation) ne sont PAS réessayées (inutile).
 * - Flush sur `beforeunload` : si une modification non encore persistée existe
 *   quand l'utilisateur ferme/quitte l'onglet, `onUnloadSave` est appelé
 *   (typiquement un PATCH `keepalive` qui survit à la navigation).
 *
 * Limitations V1 : pas de file offline (IndexedDB) — RG-PWA-02. Sera ajouté
 * avec le wizard 2D (C6) et la PWA complète.
 */

import { useEffect, useRef, useState } from "react";

type SaveStatus = "idle" | "pending" | "saving" | "saved" | "error";

/** Réessayable par défaut : erreur réseau (status 0) ou serveur (5xx). Les
 *  erreurs sans `status` numérique (exceptions inattendues) sont réessayées ;
 *  les 4xx (validation, auth) ne le sont pas. */
function defaultIsRetryable(err: unknown): boolean {
  const status = (err as { status?: unknown } | null)?.status;
  if (typeof status === "number") {
    return status === 0 || status >= 500;
  }
  return true;
}

const sleep = (ms: number) => new Promise((res) => setTimeout(res, ms));

export function useDebouncedAutosave<T>(
  payload: T,
  saveFn: (value: T) => Promise<unknown>,
  options: {
    delayMs?: number;
    /** Tant que `false`, aucune sauvegarde n'est déclenchée. La PREMIÈRE valeur
     *  reçue une fois activé sert de référence (hydratation depuis le serveur)
     *  et ne déclenche PAS de sauvegarde — seules les modifications ultérieures
     *  le font. Défaut `true`. */
    enabled?: boolean;
    /** Nombre de tentatives supplémentaires après l'échec initial (défaut 2). */
    maxRetries?: number;
    /** Délai de base du back-off, doublé à chaque tentative (défaut 600 ms). */
    retryBaseMs?: number;
    /** Prédicat décidant si une erreur justifie un retry. */
    isRetryable?: (err: unknown) => boolean;
    /** Sauvegarde de dernière chance sur fermeture d'onglet (fire-and-forget). */
    onUnloadSave?: (value: T) => void;
  } = {},
): { status: SaveStatus; flushNow: () => Promise<void> } {
  const {
    delayMs = 1500,
    enabled = true,
    maxRetries = 2,
    retryBaseMs = 600,
    isRetryable = defaultIsRetryable,
    onUnloadSave,
  } = options;
  const [status, setStatus] = useState<SaveStatus>("idle");
  // Devient true une fois la valeur de référence (1re valeur après activation)
  // ignorée. Empêche une sauvegarde parasite au chargement/hydratation.
  const baselineSkippedRef = useRef(false);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const inflightRef = useRef<Promise<unknown> | null>(null);
  const latestPayloadRef = useRef<T>(payload);
  // `dirty` = une modification existe qui n'a pas encore été persistée avec
  // succès. Sert au flush `beforeunload` pour ne rien envoyer si tout est sauvé.
  const dirtyRef = useRef(false);

  // Toujours suivre la dernière valeur (utile pour flushNow + beforeunload)
  latestPayloadRef.current = payload;

  // Refs « live » pour que le handler beforeunload (enregistré une fois) lise
  // toujours les dernières options sans se ré-attacher à chaque rendu.
  const onUnloadSaveRef = useRef(onUnloadSave);
  onUnloadSaveRef.current = onUnloadSave;

  useEffect(() => {
    if (!enabled) return;
    // La 1re valeur reçue après activation (état initial / hydratation depuis
    // le serveur) sert de référence et ne déclenche pas de sauvegarde.
    if (!baselineSkippedRef.current) {
      baselineSkippedRef.current = true;
      return;
    }
    dirtyRef.current = true;
    setStatus("pending");

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      void performSave();
    }, delayMs);

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [payload, delayMs, enabled]);

  // Flush de dernière chance quand l'utilisateur ferme / quitte l'onglet.
  useEffect(() => {
    const handler = () => {
      if (dirtyRef.current && onUnloadSaveRef.current) {
        onUnloadSaveRef.current(latestPayloadRef.current);
      }
    };
    window.addEventListener("beforeunload", handler);
    return () => window.removeEventListener("beforeunload", handler);
  }, []);

  /** Tente `saveFn` avec retry + back-off exponentiel sur erreur réessayable. */
  const saveWithRetry = async (value: T): Promise<void> => {
    let lastErr: unknown;
    for (let attempt = 0; attempt <= maxRetries; attempt++) {
      try {
        await saveFn(value);
        return;
      } catch (err) {
        lastErr = err;
        if (attempt >= maxRetries || !isRetryable(err)) throw err;
        await sleep(retryBaseMs * 2 ** attempt);
      }
    }
    throw lastErr;
  };

  const performSave = async () => {
    if (inflightRef.current) {
      // Une sauvegarde est déjà en cours — on attend
      try {
        await inflightRef.current;
      } catch {
        /* ignore */
      }
    }
    setStatus("saving");
    const current = latestPayloadRef.current;
    const promise = saveWithRetry(current);
    inflightRef.current = promise;
    try {
      await promise;
      // Si le payload a changé pendant la sauvegarde, on relance
      if (latestPayloadRef.current !== current) {
        setStatus("pending");
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => void performSave(), delayMs);
      } else {
        dirtyRef.current = false;
        setStatus("saved");
      }
    } catch {
      setStatus("error");
    } finally {
      inflightRef.current = null;
    }
  };

  const flushNow = async () => {
    if (timerRef.current) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    await performSave();
  };

  return { status, flushNow };
}
